import { MenuIcon } from "@xola/icons";
import { Tooltip } from "@xola/ui-kit";
import clsx from "clsx";
import React from "react";
import { DragDropContext, Draggable, Droppable, DroppableProvided, DropResult } from "react-beautiful-dnd";
import { SelectionStateWithSequence } from "types";
import { NoTranslate } from "./NoTranslate";
import { ProductImage } from "./ProductImage";
import { getProductObjectIcon } from "./ProductObjectFilter";
import { useProduct } from "@/hooks/resources/useProduct";
import { getCollectionItemResultingSequence } from "@/models/CollectionNode";
import { reorder } from "@/utils/reorder";

interface SelectedProductOrCatalogListProps {
    selectedProducts: SelectionStateWithSequence;
    selectedAllText?: string;
    selectedNoneText?: string;
    isError?: boolean;
    showDragAndDropProductList?: boolean;
    className?: string;
    onChange: (value: SelectionStateWithSequence) => void;
}

export const SelectedProductOrCatalogList = React.memo(
    ({
        selectedProducts,
        selectedAllText = "All products are selected",
        selectedNoneText = "No products selected",
        isError = false,
        showDragAndDropProductList = false,
        className = "",
        onChange,
    }: SelectedProductOrCatalogListProps) => {
        return (
            <div
                className={clsx(
                    "divide-y divide-gray-lighter rounded-lg border",
                    !showDragAndDropProductList && "max-h-40 overflow-y-auto",
                    isError ? "border-danger" : "border-gray-lighter",
                    className,
                )}
            >
                {selectedProducts.selected.length > 0 ? (
                    showDragAndDropProductList ? (
                        <ProductItemDragAndDrop selectedProducts={selectedProducts} onChange={onChange} />
                    ) : (
                        selectedProducts.selected.map((item) => <ProductItem key={item.id} id={item.id} />)
                    )
                ) : (
                    <div className="py-3 px-4 text-gray-dark" data-testid="select-product-list">
                        {selectedProducts.all ? selectedAllText : selectedNoneText}
                    </div>
                )}
            </div>
        );
    },
);

const ProductItem = ({ id }: { id: ID }) => {
    const { product } = useProduct(id);
    const productName = product?.name?.trim() ?? "";

    return (
        <span className="flex items-center py-2 px-3" data-testid="selected-product-list-item">
            <ProductImage product={{ id, name: product?.name, object: product?.object }} size="tiny" />
            {product?.object && <span className="ml-2">{getProductObjectIcon(product.object)}</span>}
            <Tooltip content={productName} disabled={productName.length <= 100}>
                <NoTranslate className="ml-2 font-bold text-black line-clamp-2">{productName}</NoTranslate>
            </Tooltip>
        </span>
    );
};

const ProductItemDragAndDrop = ({
    selectedProducts,
    onChange,
}: {
    selectedProducts: SelectionStateWithSequence;
    onChange?: (value: SelectionStateWithSequence) => void;
}) => {
    const handleDragEnd = (result: DropResult) => {
        if (!result.destination) {
            return;
        }

        const items = selectedProducts.selected;
        const reordered = reorder(items, result.source.index, result.destination.index);

        const resultingSequence = getCollectionItemResultingSequence(
            result.destination.index,
            result.source.index,
            items,
        );

        const draggedItem = items[result.source.index];
        const updatedItems = reordered.map((item) =>
            item.id === draggedItem.id ? { ...item, sequence: resultingSequence } : item,
        );

        onChange?.({ all: false, selected: updatedItems });
    };

    return (
        <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable
                droppableId="productDragAndDropList"
                renderClone={(provided, snapshot, rubric) => {
                    const item = selectedProducts.selected[rubric.source.index];
                    return (
                        <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            className="flex items-center space-x-2 bg-white shadow-lg"
                        >
                            <MenuIcon size="small" className="ml-2 cursor-grab" />
                            <ProductItem id={item.id} />
                        </div>
                    );
                }}
            >
                {(provided: DroppableProvided) => (
                    <div ref={provided.innerRef} {...provided.droppableProps}>
                        {selectedProducts.selected.map((item, index) => (
                            <Draggable key={item.id} draggableId={item.id} index={index}>
                                {(provided, snapshot) => (
                                    <div
                                        ref={provided.innerRef}
                                        {...provided.draggableProps}
                                        style={{
                                            ...provided.draggableProps.style,
                                        }}
                                        className={clsx("flex items-center space-x-2 bg-white", {
                                            "opacity-50": snapshot.isDragging,
                                        })}
                                    >
                                        <div {...provided.dragHandleProps}>
                                            <MenuIcon size="small" className="ml-2 cursor-grab" />
                                        </div>
                                        <ProductItem key={item.id} id={item.id} />
                                    </div>
                                )}
                            </Draggable>
                        ))}
                        {provided.placeholder}
                    </div>
                )}
            </Droppable>
        </DragDropContext>
    );
};
